import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useClientModules, MODULE_MAPPING } from '@/hooks/useClientModules';
import { Navigate } from 'react-router-dom';
import Header from '@/components/Layout/Header';
import Sidebar from '@/components/Layout/Sidebar';
import { PageLoading } from '@/components/ui/page-loading';
import { usePageTransition } from '@/hooks/usePageTransition';
import DashboardOverview from '@/components/Dashboard/DashboardOverview';
import TicketList from '@/components/Tickets/TicketList';
import KnowledgeBase from '@/components/Knowledge/KnowledgeBase';
import FormulariosManagement from '@/components/Formularios/FormulariosManagement';
import PlanoAcaoDashboard from '@/components/Formularios/PlanoAcaoDashboard';
import UserManagement from '@/components/Users/UserManagement';
import GroupManagement from '@/components/Groups/GroupManagement';
import ProfileManagement from '@/components/Profiles/ProfileManagement';
import ClientManagement from '@/components/Clients/ClientManagement';
import CategoriaManagement from '@/components/Categorias/CategoriaManagement';
import RequisicoesManagement from '@/components/Requisicoes/RequisicoesManagement';
import SolicitacaoManagement from '@/components/Solicitacoes/SolicitacaoManagement';
import CMDBDashboard from '@/components/CMDB/CMDBDashboard';
import AROANDiscovery from '@/components/CMDB/AROANDiscovery';
import AtivoManagement from '@/components/Ativos/AtivoManagement';
import { TipoAtivoManagement } from '@/components/Ativos/TipoAtivoManagement';
import { ContratoManagement } from '@/components/Contratos/ContratoManagement';
import FabricanteManagement from '@/components/Fabricantes/FabricanteManagement';
import { FornecedorManagement } from '@/components/Fornecedores/FornecedorManagement';
import { LocalizacaoManagement } from '@/components/Localizacoes/LocalizacaoManagement';
import { SoftwareManagement } from '@/components/Software/SoftwareManagement';
import SLAManagement from '@/components/SLAs/SLAManagement';
import { ChatBot } from '@/components/ChatBot/ChatBot';
import AccessDenied from '@/components/AccessDenied';
import AIConfiguration from './AIConfiguration';

import IncidentesManagement from '@/components/Incidentes/IncidentesManagement';
import ProblemasManagement from '@/components/Problemas/ProblemasManagement';
import MudancasManagement from '@/components/Mudancas/MudancasManagement';
import ReportsManagement from '@/components/Reports/ReportsManagement';
import MarketplaceView from '@/components/Marketplace/MarketplaceView';
import MyLicenseView from '@/components/Marketplace/MyLicenseView';

const Index = () => {
  const { user, profile, loading, profileLoading } = useAuth();
  const { hasModule, isGlobalAdmin } = useClientModules();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { isLoading: isTransitioning, loadingMessage, startTransition, endTransition } = usePageTransition();

  // Função auxiliar para verificar acesso a módulo
  const canAccess = (moduleCode: string): boolean => {
    if (isGlobalAdmin) return true;
    return hasModule(moduleCode);
  };

  const goToDashboard = () => setActiveModule('dashboard');

  // Redirecionar usuários não autenticados para /portal
  if (!loading && !user) {
    return <Navigate to="/portal" replace />;
  }

  // Aguardar carregamento do perfil para verificar role
  if (loading || profileLoading) {
    return <PageLoading message="Autenticando..." />;
  }

  // Redirecionar usuários com role "user" para /portal
  if (profile?.role === 'user') {
    return <Navigate to="/portal" replace />;
  }

  const handleModuleChange = (module: string) => {
    if (module === activeModule) return;
    
    // Mapear módulos para mensagens de loading personalizadas
    const loadingMessages: { [key: string]: string } = {
      'dashboard': 'Carregando Dashboard...',
      'incidents': 'Carregando Incidentes...',
      'requests': 'Carregando Requisições...',
      'problems': 'Carregando Problemas...',
      'changes': 'Carregando Mudanças...',
      'knowledge': 'Carregando Base de Conhecimento...',
      'formularios': 'Carregando Formulários...',
      'formularios-lista': 'Carregando Formulários...',
      'planos-acao': 'Carregando Planos de Ação...',
      'reports': 'Carregando Relatórios...',
      'users': 'Carregando Usuários...',
      'groups': 'Carregando Grupos...',
      'profiles': 'Carregando Perfis...',
      'clients': 'Carregando Clientes...',
      'categoria': 'Carregando Categorias...',
      'sla': 'Carregando SLAs...',
      'solicitacoes': 'Carregando Solicitações...',
      'cmdb': 'Carregando CMDB...',
      'ativos': 'Carregando Ativos...',
      'tipos-ativos': 'Carregando Tipos de Ativos...',
      'softwares': 'Carregando Softwares e Licenças...',
      'contratos': 'Carregando Contratos...',
      'fabricantes': 'Carregando Fabricantes...',
      'fornecedores': 'Carregando Fornecedores...',
      'localizacao': 'Carregando Localizações...',
      'acmdb': 'Carregando AROAN CMDB...',
      'marketplace': 'Carregando Marketplace...',
      'my-license': 'Carregando Licença...'
    };

    startTransition(loadingMessages[module] || 'Carregando...');
    setActiveModule(module);
    
    // Simular tempo de carregamento para permitir que o componente seja renderizado
    setTimeout(() => {
      endTransition();
    }, 500);
  };

  const renderContent = () => {
    switch (activeModule) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'admin':
        // Redirecionar para a página do console administrativo
        window.location.href = '/admin';
        return null;
      case 'incidents':
        return canAccess('incidents') 
          ? <IncidentesManagement /> 
          : <AccessDenied moduleName="Incidentes" onGoBack={goToDashboard} />;
      case 'requests':
        return canAccess('requests') 
          ? <RequisicoesManagement /> 
          : <AccessDenied moduleName="Requisições" onGoBack={goToDashboard} />;
      case 'problems':
        return canAccess('problems') 
          ? <ProblemasManagement /> 
          : <AccessDenied moduleName="Problemas" onGoBack={goToDashboard} />;
      case 'changes':
        return canAccess('changes') 
          ? <MudancasManagement /> 
          : <AccessDenied moduleName="Mudanças" onGoBack={goToDashboard} />;
      case 'knowledge':
        return canAccess('knowledge_base') 
          ? <KnowledgeBase /> 
          : <AccessDenied moduleName="Base de Conhecimento" onGoBack={goToDashboard} />;
      case 'formularios':
      case 'formularios-lista':
        return canAccess('forms') 
          ? <FormulariosManagement /> 
          : <AccessDenied moduleName="Formulários" onGoBack={goToDashboard} />;
      case 'planos-acao':
        return canAccess('forms') 
          ? <PlanoAcaoDashboard /> 
          : <AccessDenied moduleName="Planos de Ação" onGoBack={goToDashboard} />;
      case 'reports':
        return canAccess('reports') 
          ? <ReportsManagement /> 
          : <AccessDenied moduleName="Relatórios" onGoBack={goToDashboard} />;
      case 'users':
        return <UserManagement />;
      case 'groups':
        return <GroupManagement />;
      case 'profiles':
        return <ProfileManagement />;
      case 'clients':
        return <ClientManagement />;
      case 'categoria':
        return <CategoriaManagement />;
      case 'sla':
        return canAccess('sla_management') 
          ? <SLAManagement /> 
          : <AccessDenied moduleName="SLA" onGoBack={goToDashboard} />;
      case 'ai-config':
        return <AIConfiguration />;
      case 'solicitacoes':
        return <SolicitacaoManagement />;
      case 'cmdb':
        return (canAccess('asset_management') || canAccess('software_management'))
          ? <CMDBDashboard /> 
          : <AccessDenied moduleName="CMDB" onGoBack={goToDashboard} />;
      case 'ativos':
        return canAccess('asset_management') 
          ? <AtivoManagement /> 
          : <AccessDenied moduleName="Ativos" onGoBack={goToDashboard} />;
      case 'tipos-ativos':
        return canAccess('asset_management') 
          ? <TipoAtivoManagement /> 
          : <AccessDenied moduleName="Tipos de Ativos" onGoBack={goToDashboard} />;
      case 'softwares':
        return canAccess('software_management') 
          ? <SoftwareManagement /> 
          : <AccessDenied moduleName="Softwares e Licenças" onGoBack={goToDashboard} />;
      case 'contratos':
        return canAccess('asset_management') 
          ? <ContratoManagement /> 
          : <AccessDenied moduleName="Contratos" onGoBack={goToDashboard} />;
      case 'fabricantes':
        return canAccess('asset_management') 
          ? <FabricanteManagement /> 
          : <AccessDenied moduleName="Fabricantes" onGoBack={goToDashboard} />;
      case 'fornecedores':
        return canAccess('asset_management') 
          ? <FornecedorManagement /> 
          : <AccessDenied moduleName="Fornecedores" onGoBack={goToDashboard} />;
      case 'localizacao':
        return canAccess('asset_management')
          ? <LocalizacaoManagement />
          : <AccessDenied moduleName="Localização" onGoBack={goToDashboard} />;
      case 'acmdb':
        return canAccess('asset_management')
          ? <AROANDiscovery />
          : <AccessDenied moduleName="AROAN CMDB" onGoBack={goToDashboard} />;
      case 'marketplace':
        return <MarketplaceView />;
      case 'my-license':
        return <MyLicenseView onNavigateToMarketplace={() => handleModuleChange('marketplace')} />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="h-screen min-h-screen bg-gray-50 flex w-full overflow-hidden relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-no-repeat bg-right bg-contain opacity-40 pointer-events-none z-0"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundPosition: 'right center',
          backgroundSize: 'auto 100%'
        }}
      />
        <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        activeModule={activeModule}
        onModuleChange={handleModuleChange}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden relative z-10">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
          isSidebarOpen={isSidebarOpen}
          onCloseSidebar={() => setIsSidebarOpen(false)}
        />
        
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
          {renderContent()}
        </main>
      </div>
      
      {/* Chat Bot */}
      <ChatBot />
      
      {/* Page Loading Overlay */}
      {isTransitioning && <PageLoading message={loadingMessage} />}
    </div>
  );
};

export default Index;
